import { ChatMessageItem } from "@aidkitorg/types/lib/convoai/conversation";
import React, { useContext } from "react";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import slugid from 'slugid';
import { v4 } from 'uuid';
import { safeParse } from "../../Util";
import zod from 'zod';
import { ChatConversationContext } from "./ConversationContext";

export type ChatContextType = {

  /**
   * Sends a message to the chat
   */
  sendMessage(message: ChatMessageItem, opts?: { changeMessageId?: string, createResponse?: boolean, modalities?: ['audio', 'text'] | ['text'] }): void,

  /**
   * Indicates the chat is capable of being active and ready.
   * This is the first flag that will flip when a chat is enabled,
   * but shortly after the `active` and `ready` flags should also be flipped.
   */
  enabled: boolean,

  /**
   * Indicates the chat is actively operational.
   */
  active: boolean,

  /**
   *  Indicates the chat is ready for input
   */
  ready: boolean,

  /**
   * Registers a tool that the chat assistant may decide to use when prompted in specific ways.
   */
  registerTool<T extends zod.ZodObject<any>, Out>(tool: Tool<T, Out>): void,

  /**
   * The currently registered tools that the chat assitant knows about.
   */
  tools: Record<string, Tool<any, any>>,

  /**
   * Enables the chat,
   * which leads to the activation and ready-ing of the chat as well.
   */
  setEnabled(enable: boolean): void
};

export const ChatContext = React.createContext<ChatContextType>({
  active: false,
  ready: false,
  enabled: false,
  tools: {},
  sendMessage() { console.warn('no chat context setup, ignoring message'); },
  registerTool() { console.warn('no chat context setup, ignoring registerTool'); },
  setEnabled() { console.warn('no chat context setup, ignoring setEnabled'); },
});

export type Tool<T extends zod.ZodObject<any>, Out> = {
  name: string,
  description: string,
  schema: T,
  fn: (args: zod.infer<T>) => Promise<Out>
};

export function useTools({ sendMessage, tools }: Pick<ChatContextType, 'sendMessage' | 'tools'>) {
  const { toolCalls, messages } = useContext(ChatConversationContext);

  useAsyncEffect(async () => {
    for (const id of Array.from(toolCalls ?? [])) {
      const message = messages[id];

      if (!message) {
        console.error('[AI CHAT] no message found for function call to reference.');
        continue;
      } else if (message.type !== 'tool_call') {
        console.error('[AI CHAT] somehow a non-function-call id got mapped to a function call arg');
        continue;
      } else if (!message.name) {
        console.error('[AI CHAT] no tool name specified in original message');
        continue;
      }

      const tool = tools[message.name];

      if (!tool) {
        console.error('no tool found with the name', message.name);
        continue;
      }

      if (!message.arguments) {
        console.error('[AI CHAT] no arguments provided for function call', message);
        continue;
      }

      // using a symbol guarantees a unique identifier for a bad outcome
      const badResultSymbol = Symbol.for('BadArgFormat');
      const args = safeParse(message.arguments, badResultSymbol);

      if (args === badResultSymbol) {
        console.error('[AI CHAT] failed to parse arguments from:', message.arguments);
        continue;
      }

      console.log('[AI CHAT] executing tool call', tool.name, 'with arguments', args);

      let result: any;
      try {
        result = await tool.fn(args);
      } catch (err: any) {
        result = { error: err?.message || err };
      }

      console.debug('[AI CHAT] tool call result:', result, JSON.stringify(result));

      sendMessage({
        id: slugid.encode(v4()),
        previousItemId: message.id,
        type: 'tool_call_output',
        callId: message.callId,
        output: JSON.stringify(result),
      }, { createResponse: true });
    }
  }, [toolCalls, tools, messages]);
}
